import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { TabBar } from '../components/TabBar';
import { FilterChip } from '../components/FilterChip';
import {
  Severity,
  AlertStatus,
  AlertItem,
  InboxTab,
  FilterType
} from './Alerts.types';
import {
  DEMO_ALERTS,
  SEV_COUNTS,
  SEV_NEW,
  SEV_LABELS,
  FILTER_TYPES
} from './Alerts.demoData';
import { useAlerts } from 'apiTyped/hooks/v2.alerts.query';
import { useV2Filters } from '../context/V2FiltersContext';

// --- Component ---

export const V2Alerts: React.FC = () => {
  const { filters } = useV2Filters();
  const [selectedSev, setSelectedSev] = useState<Severity | null>('crit');
  const [activeTab, setActiveTab] = useState<InboxTab>('unread');
  const [activeFilters, setActiveFilters] = useState<FilterType[]>([
    'Distribution',
    'Competitive'
  ]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    alerts: apiAlerts,
    sevCounts: apiSevCounts,
    sevNew: apiSevNew,
    filterTypes: apiFilterTypes,
    isLoading
  } = useAlerts({
    severity: selectedSev,
    status:
      activeTab === 'all'
        ? null
        : activeTab === 'unread'
          ? 'unread'
          : activeTab,
    types: activeFilters.length > 0 ? activeFilters : undefined,
    search: searchTerm || undefined,
    market: filters.market
  });

  // Use API data when available, demo data as fallback during development
  const alerts: AlertItem[] = apiAlerts.length > 0 ? apiAlerts : DEMO_ALERTS;
  const sevCounts = apiSevCounts.crit > 0 ? apiSevCounts : SEV_COUNTS;
  const sevNew = apiSevNew.crit > 0 ? apiSevNew : SEV_NEW;
  const filterTypes = apiFilterTypes.length > 0 ? apiFilterTypes : FILTER_TYPES;

  const [alertStatuses, setAlertStatuses] = useState<
    Record<string, AlertStatus>
  >(Object.fromEntries(DEMO_ALERTS.map((a) => [a.id, a.status])));

  const toggleFilter = useCallback((filter: FilterType) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  }, []);

  const toggleRead = useCallback((id: string) => {
    setAlertStatuses((prev) => ({
      ...prev,
      [id]: prev[id] === 'unread' ? 'read' : 'unread'
    }));
  }, []);

  // Filter alerts
  const filteredAlerts = DEMO_ALERTS.filter((alert) => {
    if (activeTab === 'unread' && alertStatuses[alert.id] !== 'unread')
      return false;
    if (activeTab === 'snoozed' && alertStatuses[alert.id] !== 'snoozed')
      return false;
    if (activeTab === 'resolved' && alertStatuses[alert.id] !== 'resolved')
      return false;
    if (selectedSev && alert.severity !== selectedSev) return false;
    if (
      searchTerm &&
      !alert.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  // Group alerts
  const groups = filteredAlerts.reduce<Record<string, AlertItem[]>>(
    (acc, alert) => {
      if (!acc[alert.group]) acc[alert.group] = [];
      acc[alert.group].push(alert);
      return acc;
    },
    {}
  );

  const tabCounts = {
    unread: DEMO_ALERTS.filter((a) => alertStatuses[a.id] === 'unread').length,
    all: DEMO_ALERTS.length,
    snoozed: DEMO_ALERTS.filter((a) => alertStatuses[a.id] === 'snoozed')
      .length,
    resolved: DEMO_ALERTS.filter((a) => alertStatuses[a.id] === 'resolved')
      .length
  };

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Alerts"
        titleExtra={
          <LiveBadge>
            <LiveDot />
            Live
          </LiveBadge>
        }
        subtitle="Triage everything the platform is watching — POS movement, competitive shifts, pricing anomalies, sommelier changes, and data-quality issues."
      >
        <Button icon={<SettingsIcon />}>Alert rules</Button>
        <Button variant="primary" icon={<ActivityIcon />}>
          Subscribe
        </Button>
      </PageHeader>

      {/* Severity Strip */}
      <SevStrip>
        {(['crit', 'warn', 'info', 'good'] as Severity[]).map((sev) => (
          <SevCard
            key={sev}
            $sev={sev}
            $selected={selectedSev === sev}
            onClick={() => setSelectedSev(selectedSev === sev ? null : sev)}
          >
            <SevHead>
              <SevDot $sev={sev} />
              <SevLabel>{SEV_LABELS[sev]}</SevLabel>
            </SevHead>
            <SevValue>{SEV_COUNTS[sev]}</SevValue>
            <SevSub>
              <strong>{SEV_NEW[sev]} new</strong> in last 24h
            </SevSub>
          </SevCard>
        ))}
      </SevStrip>

      {/* Filter Row */}
      <FilterRow>
        <FilterLabel>Type</FilterLabel>
        {FILTER_TYPES.map((f) => (
          <FilterChip
            key={f}
            value={f}
            active={activeFilters.includes(f)}
            onClick={() => toggleFilter(f)}
          />
        ))}
        <FilterSep />
        <SearchInput
          placeholder="Search alerts…"
          value={searchTerm}
          onChange={(val) => setSearchTerm(val)}
        />
      </FilterRow>

      {/* Inbox Tabs */}
      <TabBar
        tabs={(['unread', 'all', 'snoozed', 'resolved'] as InboxTab[]).map(
          (tab) => ({
            id: tab,
            label: `${tab.charAt(0).toUpperCase() + tab.slice(1)} (${
              tabCounts[tab]
            })`
          })
        )}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as InboxTab)}
        meta={<>Auto-refresh every 5 min · Last update 14:23</>}
      />

      {/* Alert List */}
      <AlertsList>
        {Object.entries(groups).map(([group, alerts]) => (
          <React.Fragment key={group}>
            <AlertGroupHead>
              {group}
              <GroupLine />
            </AlertGroupHead>
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                $sev={alert.severity}
                $read={alertStatuses[alert.id] === 'read'}
                onClick={() => toggleRead(alert.id)}
              >
                <AlertInner>
                  <AlertMain>
                    <AlertMetaLine>
                      <AlertSevTag $sev={alert.severity}>
                        ● {SEV_LABELS[alert.severity]}
                      </AlertSevTag>
                      <AlertType>{alert.type}</AlertType>
                      <AlertWhen $unread={alertStatuses[alert.id] === 'unread'}>
                        {alert.when}
                      </AlertWhen>
                    </AlertMetaLine>
                    <AlertTitle>{alert.title}</AlertTitle>
                    <AlertDesc>{alert.description}</AlertDesc>
                    <AlertContext>
                      {alert.context.map((ctx, i) => (
                        <CtxChip key={i}>{ctx}</CtxChip>
                      ))}
                    </AlertContext>
                  </AlertMain>
                  <AlertChart>
                    <ChartLabel>{alert.chart.label}</ChartLabel>
                    <ChartValue $direction={alert.chart.direction}>
                      {alert.chart.value}
                    </ChartValue>
                    <MiniSparkline $direction={alert.chart.direction} />
                  </AlertChart>
                  <AlertActions onClick={(e) => e.stopPropagation()}>
                    {alert.actions.map((action, i) => (
                      <Button
                        key={i}
                        variant={action.primary ? 'primary' : 'default'}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </AlertActions>
                </AlertInner>
              </AlertCard>
            ))}
          </React.Fragment>
        ))}
        {Object.keys(groups).length === 0 && (
          <EmptyState message="No alerts match the current filters." />
        )}
      </AlertsList>
    </Page>
  );
};

// --- Icons ---

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

// Mini sparkline placeholder
const MiniSparkline: React.FC<{ $direction: 'up' | 'down' | 'flat' }> = ({
  $direction
}) => {
  const color =
    $direction === 'up'
      ? 'var(--olive)'
      : $direction === 'down'
        ? 'var(--crimson)'
        : 'var(--ink-muted)';
  const path =
    $direction === 'down'
      ? 'M0,8 L20,10 L40,12 L60,15 L80,20 L100,26 L120,30 L140,33'
      : $direction === 'up'
        ? 'M0,28 L20,25 L40,22 L60,18 L80,14 L100,10 L120,7 L140,4'
        : 'M0,18 L20,16 L40,20 L60,14 L80,22 L100,16 L120,14 L140,17';
  return (
    <SparkSvg viewBox="0 0 140 36" preserveAspectRatio="none">
      <path
        d={path}
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="140"
        cy={path.split(' ').pop()!.split(',')[1]}
        r="3"
        fill={color}
      />
    </SparkSvg>
  );
};

// --- Styled Components ---

const Page = styled.div`
  padding: 24px 0;
`;

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--olive);
  background: var(--olive-soft);
  padding: 3px 10px;
  border-radius: 100px;
`;

const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--olive);
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

// --- Severity Strip ---

const SevStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 22px;

  ${media.sm} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${media.md} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SevCard = styled.div<{ $sev: Severity; $selected: boolean }>`
  background: ${({ $selected }) =>
    $selected ? 'var(--paper-warm)' : 'var(--white)'};
  padding: 20px 22px;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;

  &:hover {
    background: var(--paper-warm);
  }

  ${({ $selected }) =>
    $selected &&
    `&::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 22px;
      right: 22px;
      height: 2px;
      background: var(--ink);
    }`}
`;

const SevHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const sevDotColors: Record<Severity, { bg: string; shadow: string }> = {
  crit: { bg: 'var(--crimson)', shadow: 'rgba(156, 42, 26, 0.12)' },
  warn: { bg: 'var(--amber)', shadow: 'rgba(184, 134, 42, 0.12)' },
  info: { bg: 'var(--indigo)', shadow: 'rgba(58, 74, 107, 0.12)' },
  good: { bg: 'var(--olive)', shadow: 'rgba(74, 107, 58, 0.12)' }
};

const SevDot = styled.span<{ $sev: Severity }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $sev }) => sevDotColors[$sev].bg};
  box-shadow: 0 0 0 4px ${({ $sev }) => sevDotColors[$sev].shadow};
`;

const SevLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-muted);
  font-weight: 600;
`;

const SevValue = styled.div`
  font-family: var(--font-serif);
  font-size: 36px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.02em;
  margin-bottom: 4px;
`;

const SevSub = styled.div`
  font-size: 12px;
  color: var(--ink-faint);

  strong {
    color: var(--ink-muted);
  }
`;

// --- Filter Row ---

const FilterRow = styled.section`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
  padding-right: 6px;
`;

const FilterSep = styled.div`
  width: 1px;
  height: 22px;
  background: var(--line);
  margin: 0 4px;
`;

// --- Alerts List ---

const AlertsList = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AlertGroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 4px 8px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  font-weight: 600;

  &:first-child {
    padding-top: 0;
  }
`;

const GroupLine = styled.span`
  flex: 1;
  height: 1px;
  background: var(--line);
`;

const sevBorderColors: Record<Severity, string> = {
  crit: 'var(--crimson)',
  warn: 'var(--amber)',
  info: 'var(--indigo)',
  good: 'var(--olive)'
};

const AlertCard = styled.article<{ $sev: Severity; $read: boolean }>`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 0;
  position: relative;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
  opacity: ${({ $read }) => ($read ? 0.65 : 1)};

  &:hover {
    border-color: var(--ink-faint);
    box-shadow: var(--shadow-md);
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${({ $sev }) => sevBorderColors[$sev]};
  }
`;

const AlertInner = styled.div`
  padding: 18px 22px 18px 26px;
  display: grid;
  grid-template-columns: 1fr 180px auto;
  gap: 20px;
  align-items: center;

  ${media.md} {
    grid-template-columns: 1fr 180px auto;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const AlertMain = styled.div`
  min-width: 0;
`;

const AlertMetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
`;

const sevTagColors: Record<Severity, { bg: string; color: string }> = {
  crit: { bg: 'var(--crimson-soft)', color: 'var(--crimson)' },
  warn: { bg: 'var(--amber-soft)', color: 'var(--amber)' },
  info: { bg: 'var(--indigo-soft)', color: 'var(--indigo)' },
  good: { bg: 'var(--olive-soft)', color: 'var(--olive)' }
};

const AlertSevTag = styled.span<{ $sev: Severity }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  background: ${({ $sev }) => sevTagColors[$sev].bg};
  color: ${({ $sev }) => sevTagColors[$sev].color};
`;

const AlertType = styled.span`
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 10.5px;
  font-weight: 600;
`;

const AlertWhen = styled.span<{ $unread: boolean }>`
  font-family: var(--font-mono);
  text-transform: none;
  letter-spacing: 0;
  margin-left: auto;
  font-weight: 500;

  ${({ $unread }) =>
    $unread &&
    `&::before {
      content: "";
      display: inline-block;
      width: 6px;
      height: 6px;
      background: var(--terracotta);
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: 1px;
    }`}
`;

const AlertTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 19px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.25;
  margin: 0 0 6px;
`;

const AlertDesc = styled.p`
  font-size: 13.5px;
  color: var(--ink-muted);
  line-height: 1.5;
  margin: 0;
`;

const AlertContext = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const CtxChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  background: var(--paper);
  border-radius: 100px;
  font-size: 11.5px;
  color: var(--ink-muted);
  font-weight: 500;
`;

const AlertChart = styled.div`
  background: var(--paper);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChartLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
`;

const ChartValue = styled.div<{ $direction: 'up' | 'down' | 'flat' }>`
  font-family: var(--font-mono);
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${({ $direction }) =>
    $direction === 'up'
      ? 'var(--olive)'
      : $direction === 'down'
        ? 'var(--crimson)'
        : 'var(--ink-muted)'};
`;

const SparkSvg = styled.svg`
  width: 100%;
  height: 36px;
`;

const AlertActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;
